import React, { useState, useEffect } from 'react';
import {
  Container,
  Heading,
  Text,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardFooter,
  Image,
  Button,
  Input,
  Select,
  Box,
  Flex,
  Badge,
  Spinner,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  useToast,
  Wrap,
  WrapItem,
  Stack,
  FormControl,
  FormLabel,
  Textarea,
  Icon,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
  VStack,
  IconButton
} from '@chakra-ui/react';
import { PhoneIcon, ChevronRightIcon, SearchIcon, RepeatIcon, AddIcon, CloseIcon, ChevronLeftIcon, ChevronRightIcon as RightArrowIcon } from '@chakra-ui/icons';
import { FaLeaf, FaFilter, FaMapMarkerAlt } from 'react-icons/fa';
import { API_BASE_URL } from '../../config/api_keys';

const AdminStore = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { isOpen: isDialogOpen, onOpen: openDialog, onClose: closeDialog } = useDisclosure();
  const { isOpen: isOrderDialogOpen, onOpen: openOrderDialog, onClose: closeOrderDialog } = useDisclosure();
  const { isOpen: isFiltersOpen, onOpen: openFilters, onClose: closeFilters } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    type: '',
    crop: '',
    search: ''
  });
  
  // Order form state
  const [orderForm, setOrderForm] = useState({
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    location: null
  });
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState('');
  const [orderError, setOrderError] = useState('');

  // Image carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const productTypes = [
    'Fertilizer',
    'Pesticide',
    'Herbicide',
    'Fungicide',
    'Insecticide',
    'Growth Regulator'
  ];

  const allowedCrops = ['Gehu', 'Dhan', 'Baingan', 'Laal Mirch', 'Makka'];

  useEffect(() => {
    fetchProducts();
  }, [page, filters]);

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const authToken = localStorage.getItem('authToken');
      const queryParams = new URLSearchParams({
        page,
        limit: 12,
        ...(filters.type && { type: filters.type }),
        ...(filters.crop && { crop: filters.crop }),
        ...(filters.search && { search: filters.search })
      }).toString();

      const response = await fetch(`${API_BASE_URL}/api/admin/products?${queryParams}`, {
        headers: {
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        }
      });
      const data = await response.json();
      
      if (response.ok) {
        setProducts(data.products);
        setTotalPages(Math.ceil(data.total / data.limit));
      } else {
        setError(data.error || 'Failed to fetch products');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleSearch = (e) => {
    handleFilterChange('search', e.target.value);
  };

  const clearFilters = () => {
    setFilters({
      type: '',
      crop: '',
      search: ''
    });
    setPage(1);
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0); // Reset image index when viewing a new product
    openDialog();
  };

  const handleOrderProduct = () => {
    closeDialog();
    openOrderDialog();
    // Prefill with any existing data if available
    setOrderForm({
      customerName: '',
      customerPhone: '',
      customerAddress: '',
      location: null
    });
  };

  const handleOrderFormChange = (field, value) => {
    setOrderForm(prev => ({ ...prev, [field]: value }));
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setOrderForm(prev => ({
            ...prev,
            location: {
              type: 'Point',
              coordinates: [position.coords.longitude, position.coords.latitude]
            }
          }));
          
          // Show success message with coordinates
          toast({
            title: 'Location Retrieved',
            description: `Lat: ${position.coords.latitude.toFixed(4)}, Lng: ${position.coords.longitude.toFixed(4)}`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          toast({
            title: 'Location Error',
            description: 'Unable to get your location. Please enter your address manually.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      );
    } else {
      toast({
        title: 'Location Not Supported',
        description: 'Geolocation is not supported by this browser.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const submitOrder = async () => {
    // Validation
    if (!orderForm.customerName || !orderForm.customerPhone || !orderForm.customerAddress) {
      setOrderError('Please fill in all required fields');
      return;
    }

    // Validate phone number
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(orderForm.customerPhone.replace(/[-\s]/g, ''))) {
      setOrderError('Please enter a valid Indian mobile number');
      return;
    }

    setOrderLoading(true);
    setOrderError('');
    setOrderSuccess('');

    try {
      const authToken = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/product-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        body: JSON.stringify({
          ...orderForm,
          productId: selectedProduct._id
        })
      });

      const data = await response.json();

      if (response.ok) {
        setOrderSuccess('Order placed successfully! Our team will contact you shortly.');
        // Reset form
        setOrderForm({
          customerName: '',
          customerPhone: '',
          customerAddress: '',
          location: null
        });
        // Close dialog after delay
        setTimeout(() => {
          closeOrderDialog();
        }, 2000);
      } else {
        setOrderError(data.error || 'Failed to place order');
      }
    } catch (err) {
      setOrderError('Network error. Please try again.');
    } finally {
      setOrderLoading(false);
    }
  };

  // Image carousel functions
  const nextImage = () => {
    if (selectedProduct && selectedProduct.images && selectedProduct.images.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === selectedProduct.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProduct && selectedProduct.images && selectedProduct.images.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? selectedProduct.images.length - 1 : prevIndex - 1
      );
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      {/* Header */}
      <Box textAlign="center" mb={8}>
        <Box 
          display="inline-flex" 
          alignItems="center" 
          justifyContent="center"
          bg="yellow.400"
          px={4}
          py={2}
          borderRadius="full"
          mb={4}
        >
          <Icon as={FaLeaf} mr={2} color="green.800" />
          <Heading as="h1" size="lg" fontWeight="bold" color="green.800">
            Admin Store
          </Heading>
        </Box>
        <Text color="gray.600" mb={6}>
          Premium agricultural products curated by our experts
        </Text>
      </Box>

      {/* Search and Filters */}
      <Box mb={6}>
        <Grid templateColumns={{ base: "1fr", md: "2fr 1fr 1fr" }} gap={4} alignItems="center">
          <Input
            placeholder="Search Products"
            value={filters.search}
            onChange={handleSearch}
          />
          <Button
            leftIcon={<FaFilter />}
            onClick={isFiltersOpen ? closeFilters : openFilters}
            variant="outline"
          >
            Filters
          </Button>
          <Button
            onClick={clearFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
        </Grid>

        {/* Advanced Filters */}
        {isFiltersOpen && (
          <Box mt={4} p={4} borderWidth="1px" borderRadius="md">
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
              <FormControl>
                <FormLabel>Product Type</FormLabel>
                <Select
                  placeholder="All Types"
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  {productTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Crop</FormLabel>
                <Select
                  placeholder="All Crops"
                  value={filters.crop}
                  onChange={(e) => handleFilterChange('crop', e.target.value)}
                >
                  {allowedCrops.map(crop => (
                    <option key={crop} value={crop}>{crop}</option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Error Message */}
      {error && (
        <Box mb={4}>
          <Text color="red.500">{error}</Text>
        </Box>
      )}

      {/* Products Grid */}
      {loading ? (
        <Flex justify="center" py={12}>
          <Spinner size="xl" />
        </Flex>
      ) : products.length === 0 ? (
        <Box textAlign="center" py={12}>
          <Text fontSize="lg" color="gray.600">
            No products found
          </Text>
          <Text color="gray.500">
            Try adjusting your filters or check back later
          </Text>
        </Box>
      ) : (
        <>
          <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }} gap={6}>
            {products.map(product => (
              <Card 
                key={product._id} 
                borderWidth="1px" 
                _hover={{ shadow: "md", borderColor: "yellow.400" }}
              >
                {product.images && product.images.length > 0 ? (
                  <Box position="relative">
                    <Image
                      src={product.images[0]}
                      alt={product.name}
                      height="160px"
                      objectFit="cover"
                    />
                    {product.images.length > 1 && (
                      <Badge 
                        position="absolute" 
                        top={2} 
                        right={2} 
                        colorScheme="yellow"
                      >
                        {product.images.length} images
                      </Badge>
                    )}
                  </Box>
                ) : (
                  <Flex 
                    height="160px" 
                    bg="gray.100" 
                    alignItems="center" 
                    justifyContent="center"
                  >
                    <Icon as={FaLeaf} fontSize="4xl" color="gray.400" />
                  </Flex>
                )}
                <CardBody>
                  <Flex justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Heading size="md" fontWeight="bold">
                      {product.name}
                    </Heading>
                    <Badge 
                      colorScheme="yellow" 
                      variant="solid"
                    >
                      {product.type}
                    </Badge>
                  </Flex>
                  
                  <Wrap mb={2}>
                    {product.crops && product.crops.map((crop, index) => (
                      <WrapItem key={index}>
                        <Badge colorScheme="green">{crop}</Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                  
                  <Flex justifyContent="space-between" alignItems="center" mt={3}>
                    <Box>
                      <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                        MRP: ₹{product.mrp}
                      </Text>
                      <Text fontWeight="bold" color="green.600">
                        ₹{product.sellPrice}
                      </Text>
                    </Box>
                  </Flex>
                </CardBody>
                <CardFooter justifyContent="center">
                  <Button 
                    colorScheme="yellow" 
                    onClick={() => handleViewProduct(product)}
                    rightIcon={<ChevronRightIcon />}
                  >
                    View Details
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Flex justify="center" mt={8}>
              <Button
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                isDisabled={page === 1}
                mr={2}
              >
                Previous
              </Button>
              <Text alignSelf="center" mx={4}>
                Page {page} of {totalPages}
              </Text>
              <Button
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                isDisabled={page === totalPages}
                ml={2}
              >
                Next
              </Button>
            </Flex>
          )}
        </>
      )}

      {/* Product Detail Dialog */}
      <AlertDialog
        isOpen={isDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDialog}
        size="xl"
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxWidth="600px">
            {selectedProduct && (
              <>
                <AlertDialogHeader>
                  <Flex justifyContent="space-between" alignItems="center">
                    <Heading size="lg">{selectedProduct.name}</Heading>
                    <Button onClick={closeDialog}>
                      <CloseIcon />
                    </Button>
                  </Flex>
                </AlertDialogHeader>
                <AlertDialogBody>
                  <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
                    <Box>
                      {/* Image Carousel */}
                      <Box position="relative" mb={4}>
                        {selectedProduct.images && selectedProduct.images.length > 0 ? (
                          <>
                            <Image
                              src={selectedProduct.images[currentImageIndex]}
                              alt={`${selectedProduct.name} - Image ${currentImageIndex + 1}`}
                              borderRadius="md"
                              width="100%"
                              height="300px"
                              objectFit="cover"
                            />
                            {selectedProduct.images.length > 1 && (
                              <>
                                <IconButton
                                  icon={<ChevronLeftIcon />}
                                  position="absolute"
                                  left={2}
                                  top="50%"
                                  transform="translateY(-50%)"
                                  onClick={prevImage}
                                  aria-label="Previous image"
                                  colorScheme="yellow"
                                />
                                <IconButton
                                  icon={<RightArrowIcon />}
                                  position="absolute"
                                  right={2}
                                  top="50%"
                                  transform="translateY(-50%)"
                                  onClick={nextImage}
                                  aria-label="Next image"
                                  colorScheme="yellow"
                                />
                                <Box 
                                  position="absolute" 
                                  bottom={2} 
                                  left="50%" 
                                  transform="translateX(-50%)"
                                  bg="rgba(0,0,0,0.5)"
                                  color="white"
                                  px={2}
                                  py={1}
                                  borderRadius="md"
                                  fontSize="sm"
                                >
                                  {currentImageIndex + 1} / {selectedProduct.images.length}
                                </Box>
                              </>
                            )}
                          </>
                        ) : (
                          <Flex 
                            height="300px" 
                            bg="gray.100" 
                            alignItems="center" 
                            justifyContent="center"
                            borderRadius="md"
                          >
                            <Icon as={FaLeaf} fontSize="6xl" color="gray.400" />
                          </Flex>
                        )}
                      </Box>
                      
                      <Box mt={4}>
                        <Heading size="md" mb={2}>Pricing</Heading>
                        <Flex alignItems="center">
                          <Text textDecoration="line-through" color="gray.500" mr={3}>
                            MRP: ₹{selectedProduct.mrp}
                          </Text>
                          <Heading size="md" color="green.600">
                            ₹{selectedProduct.sellPrice}
                          </Heading>
                        </Flex>
                      </Box>
                    </Box>
                    
                    <Box>
                      <Flex justifyContent="space-between" alignItems="center" mb={4}>
                        <Badge 
                          colorScheme="yellow" 
                          fontSize="lg" 
                          px={3} 
                          py={1}
                        >
                          {selectedProduct.type}
                        </Badge>
                        {selectedProduct.isOrganic && (
                          <Badge 
                            colorScheme="green" 
                            fontSize="lg" 
                            px={3} 
                            py={1}
                          >
                            Organic
                          </Badge>
                        )}
                      </Flex>
                      
                      <Text mb={4}>
                        {selectedProduct.description}
                      </Text>
                      
                      <Box mb={4}>
                        <Heading size="sm" mb={2}>Manufacturer</Heading>
                        <Text>
                          {selectedProduct.manufacturer || 'Not specified'}
                        </Text>
                      </Box>
                      
                      {selectedProduct.crops && selectedProduct.crops.length > 0 && (
                        <Box mb={4}>
                          <Heading size="sm" mb={2}>Suitable for Crops</Heading>
                          <Wrap>
                            {selectedProduct.crops.map((crop, index) => (
                              <WrapItem key={index}>
                                <Badge colorScheme="green">{crop}</Badge>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </Box>
                      )}
                      
                      {selectedProduct.diseases && selectedProduct.diseases.length > 0 && (
                        <Box mb={4}>
                          <Heading size="sm" mb={2}>Target Diseases</Heading>
                          <Text>
                            {selectedProduct.diseases.join(', ')}
                          </Text>
                        </Box>
                      )}
                      
                      {selectedProduct.composition && (
                        <Box mb={4}>
                          <Heading size="sm" mb={2}>Composition</Heading>
                          <Text>
                            {selectedProduct.composition}
                          </Text>
                        </Box>
                      )}
                      
                      {selectedProduct.formulation && (
                        <Box mb={4}>
                          <Heading size="sm" mb={2}>Formulation</Heading>
                          <Text>
                            {selectedProduct.formulation}
                          </Text>
                        </Box>
                      )}
                      
                      {selectedProduct.packSizes && selectedProduct.packSizes.length > 0 && (
                        <Box mb={4}>
                          <Heading size="sm" mb={2}>Available Pack Sizes</Heading>
                          <Text>
                            {selectedProduct.packSizes.join(', ')}
                          </Text>
                        </Box>
                      )}
                      
                      {selectedProduct.recommendedDosage && (
                        <Box mb={4}>
                          <Heading size="sm" mb={2}>Recommended Dosage</Heading>
                          <Text>
                            {selectedProduct.recommendedDosage}
                          </Text>
                        </Box>
                      )}
                      
                      {selectedProduct.applicationMethod && (
                        <Box mb={4}>
                          <Heading size="sm" mb={2}>Application Method</Heading>
                          <Text>
                            {selectedProduct.applicationMethod}
                          </Text>
                        </Box>
                      )}
                      
                      {selectedProduct.frequency && (
                        <Box mb={4}>
                          <Heading size="sm" mb={2}>Frequency</Heading>
                          <Text>
                            {selectedProduct.frequency}
                          </Text>
                        </Box>
                      )}
                      
                      {selectedProduct.safetyPrecautions && (
                        <Box mb={4}>
                          <Heading size="sm" mb={2}>Safety Precautions</Heading>
                          <Text>
                            {selectedProduct.safetyPrecautions}
                          </Text>
                        </Box>
                      )}
                      
                      {selectedProduct.registrationNumber && (
                        <Box mb={4}>
                          <Heading size="sm" mb={2}>Registration Number</Heading>
                          <Text>
                            {selectedProduct.registrationNumber}
                          </Text>
                        </Box>
                      )}
                      
                      <Box 
                        p={4} 
                        bg="orange.50" 
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor="orange.200"
                      >
                        <Heading size="sm" mb={2} color="orange.600">
                          Contact for Purchase
                        </Heading>
                        <Flex alignItems="center" mb={2}>
                          <PhoneIcon mr={2} color="orange.600" />
                          <Text>
                            <a href="tel:+919452350183" style={{ color: '#dd6b20', textDecoration: 'none' }}>
                              +91 94523 50183
                            </a>
                          </Text>
                        </Flex>
                        <Text fontSize="sm" color="gray.600">
                          Call this number to inquire about the product or place an order
                        </Text>
                      </Box>
                    </Box>
                  </Grid>
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeDialog} mr={3}>
                    Close
                  </Button>
                  <Button 
                    colorScheme="yellow" 
                    onClick={handleOrderProduct}
                  >
                    Order This Product
                  </Button>
                </AlertDialogFooter>
              </>
            )}
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Order Dialog */}
      <AlertDialog
        isOpen={isOrderDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeOrderDialog}
        size="lg"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              <Flex alignItems="center">
                <Icon as={FaLeaf} mr={2} color="green.600" />
                Order {selectedProduct?.name}
              </Flex>
            </AlertDialogHeader>
            <AlertDialogBody>
              {orderSuccess ? (
                <Box>
                  <Text color="green.500" fontWeight="bold">{orderSuccess}</Text>
                </Box>
              ) : (
                <>
                  {orderError && (
                    <Box mb={4}>
                      <Text color="red.500">{orderError}</Text>
                    </Box>
                  )}
                  
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Full Name</FormLabel>
                      <Input
                        placeholder="Enter your full name"
                        value={orderForm.customerName}
                        onChange={(e) => handleOrderFormChange('customerName', e.target.value)}
                      />
                    </FormControl>
                    
                    <FormControl isRequired>
                      <FormLabel>Mobile Number</FormLabel>
                      <Input
                        placeholder="Enter your mobile number"
                        value={orderForm.customerPhone}
                        onChange={(e) => handleOrderFormChange('customerPhone', e.target.value)}
                      />
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        Enter a valid Indian mobile number
                      </Text>
                    </FormControl>
                    
                    <FormControl isRequired>
                      <FormLabel>Complete Address</FormLabel>
                      <Textarea
                        placeholder="Enter your complete address"
                        value={orderForm.customerAddress}
                        onChange={(e) => handleOrderFormChange('customerAddress', e.target.value)}
                        rows={3}
                      />
                    </FormControl>
                    
                    <Box w="100%">
                      <Button
                        leftIcon={<Icon as={FaMapMarkerAlt} />}
                        onClick={handleGetCurrentLocation}
                        variant="outline"
                        mb={2}
                      >
                        Use Current Location
                      </Button>
                      <Text fontSize="sm" color="gray.500">
                        (Optional) Helps us serve you better. Coordinates will be displayed when captured.
                      </Text>
                      {orderForm.location && (
                        <Text fontSize="sm" color="green.600" mt={1}>
                          Location captured: Lat {orderForm.location.coordinates[1].toFixed(4)}, 
                          Lng {orderForm.location.coordinates[0].toFixed(4)}
                        </Text>
                      )}
                    </Box>
                  </VStack>
                </>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              {!orderSuccess && (
                <>
                  <Button ref={cancelRef} onClick={closeOrderDialog} mr={3}>
                    Cancel
                  </Button>
                  <Button 
                    colorScheme="yellow" 
                    onClick={submitOrder}
                    isLoading={orderLoading}
                  >
                    Place Order
                  </Button>
                </>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default AdminStore;